import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";

import { supabase } from "../providers/supabase/supabase";

type Crop = { id: number; name: string };

export const CropsConfig = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [crops, setCrops] = useState<Crop[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data } = useQuery({
    queryKey: ["crops"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crops")
        .select("*")
        .order("name");
      if (error) throw error;
      return data as Crop[];
    },
  });

  useEffect(() => {
    if (data) setCrops(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const c of crops) {
        if (c.id > 0) {
          const { error } = await supabase
            .from("crops")
            .update({ name: c.name })
            .eq("id", c.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("crops")
            .insert({ name: c.name });
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      notify("Crops saved", { type: "success" });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["crops"] });
    },
    onError: () => notify("Failed to save", { type: "error" }),
  });

  const handleAdd = () => {
    setCrops((prev) => [...prev, { id: -Date.now(), name: "" }]);
    setDirty(true);
  };

  const handleDelete = async (index: number) => {
    const c = crops[index];
    if (c.id > 0) {
      const { error } = await supabase
        .from("crops")
        .delete()
        .eq("id", c.id);
      if (error) {
        notify(
          "Cannot delete — crop may have linked pricing brackets",
          { type: "error" },
        );
        return;
      }
    }
    setCrops((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
    queryClient.invalidateQueries({ queryKey: ["crops"] });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Manage crops (Oil Palm, Durian, Rubber, etc.). Pricing is
        configured per entity type + crop combination in Rate Cards.
      </p>

      <div className="space-y-2">
        {crops.map((c, i) => (
          <div key={c.id} className="flex items-center gap-2">
            <input
              type="text"
              value={c.name}
              onChange={(e) => {
                setCrops((prev) =>
                  prev.map((cr, idx) =>
                    idx === i
                      ? { ...cr, name: e.target.value }
                      : cr,
                  ),
                );
                setDirty(true);
              }}
              placeholder="Crop name"
              className="flex-1 px-3 py-1.5 text-sm border rounded-md"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(i)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-1" />
          Add Crop
        </Button>
        {dirty && (
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        )}
      </div>
    </div>
  );
};
